import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch } from 'react-redux';
import { selectUser } from 'selectors';
import { Image } from 'styled-minimal';
import { useAppSelector } from 'modules/hooks';
import { STATUS } from 'literals';
import { userAnswer } from 'actions';
import Icon from 'components/Icon';
import Typing from 'components/Typing';
import Header from 'components/Header/Header';
import SubmitModal from 'components/SubmitModal/SubmitModal';
import IdleCheckModal from 'components/IdleCheckModal/IdleCheckModal';
import ChatOrientationModal from 'components/ChatOrientationModal/ChatOrientationModal';
import AboutModal from 'components/AboutModal/AboutModal';
import AboutIntegralEdModal from 'components/AboutIntegralEdModal/AboutIntegralEdModal';
import LoadingInterview from 'components/Loading/LoadingInterview';
import { StyledBox,
  LeftSide,
  RightSide,
  ChatWrapper,
  ToggleTab,
  ToolsDrawer,
  DrawerDivider,
  DrawerFooter,
  DrawerLink,
  StyledContainer,
  InputBox,
  Input,
  Button,
  MicButton } from './chatStyle';

// The phrase the agent uses to signal the conversation summary.
// When the bot's message contains this string, the submit modal auto-appears.
const SUMMARY_TRIGGER = 'Summary of our conversation:';

// Idle handling:
//   1. After IDLE_PROMPT_MS of no exchange, open the "Still there?" modal.
//      Modal is non-destructive — show it even on short transcripts.
//   2. If the user doesn't respond to the modal within ABANDON_AFTER_PROMPT_MS,
//      AND the transcript is substantial, silently submit so the user still
//      receives their report by email.
const IDLE_PROMPT_MS = 15 * 60 * 1000; // 15 min
const ABANDON_AFTER_PROMPT_MS = 15 * 60 * 1000; // another 15 min
const ABANDON_MIN_CHARS = 1000;

function Chat() {
  const dispatch = useDispatch();
  const user = useAppSelector(selectUser);
  const { coach, messages, questions, email, sessionId, name: firstName } = user;
  const question = questions[coach];

  const chat = useRef<HTMLUListElement>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState('');

  // ── Microphone / STT ────────────────────────────────────────────────────────
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  // Prevents concurrent transcription requests when chunks overlap
  const transcribingRef = useRef(false);
  // Text that was in the input box before recording started — preserved as prefix
  const preRecordingTextRef = useRef('');
  // Incremented on every send — any transcription callback that captured an older
  // generation is stale and must not overwrite the (now-cleared) input box.
  const transcribeGenerationRef = useRef(0);

  // Sends accumulated audio chunks to /api/transcribe and updates the input.
  // Always uses the full accumulated blob so each call produces a complete transcript.
  const transcribeAccumulated = async (blobType: string) => {
    if (transcribingRef.current || audioChunksRef.current.length === 0) return;
    const generation = transcribeGenerationRef.current;
    transcribingRef.current = true;
    setIsTranscribing(true);
    try {
      const blob = new Blob(audioChunksRef.current, { type: blobType });
      const arrayBuffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce((acc, byte) => acc + String.fromCharCode(byte), ''),
      );
      // 15 s timeout so a hung /api/transcribe (ElevenLabs outage, slow upload)
      // doesn't permanently lock transcribingRef and block further mic chunks.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      let res: Response;
      try {
        res = await fetch('/api/transcribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audio: base64, mimeType: blob.type }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      const data = await res.json();
      // Discard if a send happened while this request was in-flight
      if (transcribeGenerationRef.current !== generation) return;
      if (data.success && data.text) {
        const prefix = preRecordingTextRef.current;
        setInput(prefix ? `${prefix} ${data.text}` : data.text);
      }
    } catch (err) {
      console.error('[STT] Transcription failed — interview can still proceed via text:', err);
    } finally {
      transcribingRef.current = false;
      setIsTranscribing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const effectiveMime = mimeType || recorder.mimeType || 'audio/webm';

      audioChunksRef.current = [];
      preRecordingTextRef.current = input;

      // Fire every 3 s — each event sends the full accumulated blob for transcription.
      // Skips the first tiny chunk (often just the webm header with no speech yet).
      recorder.ondataavailable = (e) => {
        if (e.data.size === 0) return;
        audioChunksRef.current.push(e.data);
        if (audioChunksRef.current.length > 1) {
          transcribeAccumulated(effectiveMime);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        // Final transcription — authoritative result over the full audio
        await transcribeAccumulated(effectiveMime);
        if (text.current) text.current.focus();
      };

      recorder.start(3000); // chunk every 3 s
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
    } catch (err) {
      console.error('[STT] Microphone access denied or unavailable:', err);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const [panelOpen, setPanelOpen] = useState(false);

  const [showChatOrientation, setShowChatOrientation] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showAboutIntegralEd, setShowAboutIntegralEd] = useState(false);

  // Show chat orientation modal once per session on first mount
  useEffect(() => {
    if (!sessionStorage.getItem('ik_chat_orientation_shown')) {
      sessionStorage.setItem('ik_chat_orientation_shown', '1');
      setShowChatOrientation(true);
    }
  }, []);

  // Modal state: 'hidden' | 'agent-triggered' | 'user-triggered' | 'idle-triggered'
  const [modalMode, setModalMode] = useState<'hidden' | 'agent' | 'user' | 'idle'>('hidden');
  // Track whether the agent trigger has already fired this session (avoid re-triggering)
  const [agentTriggered, setAgentTriggered] = useState(false);

  // Idle prompt: opens "Still there?" modal after IDLE_PROMPT_MS.
  // If the user clicks "I'm still here", bumping keepAliveTick re-runs the
  // timer effect and starts a fresh 15-min window.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abandonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [idlePromptOpen, setIdlePromptOpen] = useState(false);
  const [keepAliveTick, setKeepAliveTick] = useState(0);
  const hasAutoSubmittedRef = useRef(false);

  // Fetch the initial greeting on mount
  useEffect(() => {
    if (!question) {
      dispatch(userAnswer());
    }
  }, []);

  // Watch the current bot message for the summary trigger phrase.
  // Fires once per session — subsequent messages won't re-open the modal.
  useEffect(() => {
    if (question && question.includes(SUMMARY_TRIGGER) && !agentTriggered) {
      setAgentTriggered(true);
      hasAutoSubmittedRef.current = true; // prevent abandon timer from double-firing
      setModalMode('agent');
    }
  }, [question, agentTriggered]);

  // Reset the idle prompt timer whenever a new exchange completes OR the user
  // dismisses the idle modal with "I'm still here" (keepAliveTick bump).
  useEffect(() => {
    if (!sessionId || hasAutoSubmittedRef.current) return;

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);

    idleTimerRef.current = setTimeout(() => {
      if (hasAutoSubmittedRef.current) return;
      console.log('[Chat] Idle prompt triggered');
      setIdlePromptOpen(true);

      // Safety net: if the user never responds to the modal, silently submit
      // after another 15 min so they still receive their report by email.
      abandonTimerRef.current = setTimeout(async () => {
        if (hasAutoSubmittedRef.current) return;

        const transcriptChars = messages[coach]
          .slice(2)
          .reduce((sum, m) => sum + m.content.length, 0);

        if (transcriptChars < ABANDON_MIN_CHARS) return;

        hasAutoSubmittedRef.current = true;
        console.log(`[Chat] Abandon fallback after idle prompt — ${transcriptChars} chars`);
        try {
          await fetch('/api/submit-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              sessionId,
              firstName,
              email,
              trigger: 'abandon',
              messages: messages[coach],
            }),
          });
        } catch (err) {
          console.error('[Chat] Abandon fallback submit failed:', err);
        }
      }, ABANDON_AFTER_PROMPT_MS);
    }, IDLE_PROMPT_MS);

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (abandonTimerRef.current) clearTimeout(abandonTimerRef.current);
    };
  }, [messages[coach].length, keepAliveTick]);

  // Tab-close safety net.
  //
  // Per-turn /api/chat writes used to keep Chat_Import warm, but they were
  // silently timing out and got removed (the canonical write happens in
  // submit-session). That leaves a gap: if the user closes the tab before
  // submitting AND before the abandon timer fires, no transcript ever lands.
  //
  // navigator.sendBeacon is the browser primitive designed for exactly this
  // case — a fire-and-forget POST that the browser transmits even as the
  // page is unloading. We hit /api/save-partial which writes Chat_Import
  // but does NOT flip Interview_Complete, so partial transcripts are
  // preserved without polluting the AI pipeline.
  //
  // pagehide (not beforeunload) fires reliably on iOS Safari too.
  useEffect(() => {
    if (!sessionId || hasAutoSubmittedRef.current) return;
    const beacon = () => {
      // Skip if the user hasn't actually sent anything yet (messages[coach]
      // only has the placeholder + system prompt).
      if (messages[coach].length <= 2) return;
      try {
        const payload = new Blob(
          [JSON.stringify({
            sessionId,
            firstName,
            email,
            messages: messages[coach],
          })],
          { type: 'application/json' },
        );
        navigator.sendBeacon('/api/save-partial', payload);
      } catch (err) {
        // sendBeacon can throw on some browsers if the page is in a bad state.
        // Nothing we can do — this is a last-ditch effort by design.
        console.warn('[Chat] save-partial beacon failed:', err);
      }
    };
    window.addEventListener('pagehide', beacon);
    return () => window.removeEventListener('pagehide', beacon);
  }, [sessionId, firstName, email, messages, coach]);

  // Auto-resize textarea whenever input changes (handles transcription updates too)
  useEffect(() => {
    if (text.current) {
      text.current.style.height = 'auto';
      text.current.style.height = `${text.current.scrollHeight}px`;
    }
  }, [input]);

  // Scroll to bottom of chat when a new message arrives
  const [audioRequest, setAudioRequest] = useState(true);
  useEffect(() => {
    if (chat.current != null && user.status !== STATUS.IDLE) {
      if (audioRequest) {
        setAudioRequest(false);
      } else {
        chat.current.scrollTop = chat.current.scrollHeight - chat.current.clientHeight;
      }
    }
    if (text.current != null) {
      text.current.focus();
    }
  }, [question, user.status]);

  const messageList = messages[coach].slice(2).map(({ content }) => content);

  // Show the branded loading screen until the first bot question arrives.
  // After that, the inline Typing dots take over for subsequent turns.
  const isInitialLoading = !question && messageList.length === 0;

  const handleSend = () => {
    // Invalidate any in-flight transcription and stop recording so the cleared
    // input box cannot be repopulated by a stale onstop/ondataavailable callback.
    transcribeGenerationRef.current += 1;
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);
    }
    audioChunksRef.current = [];
    preRecordingTextRef.current = '';
    dispatch(userAnswer(input));
    setInput('');
  };

  const handleEnter = (event: React.KeyboardEvent) => {
    if (!event.altKey && !event.shiftKey && !event.ctrlKey && event.key === 'Enter') {
      event.preventDefault();
      handleSend();
    }
  };

  const handleSubmitClick = () => {
    hasAutoSubmittedRef.current = true; // prevent abandon timer from double-firing
    setModalMode('user');
  };

  const handleIdleContinue = () => {
    setIdlePromptOpen(false);
    if (abandonTimerRef.current) {
      clearTimeout(abandonTimerRef.current);
      abandonTimerRef.current = null;
    }
    setKeepAliveTick(t => t + 1); // re-runs the idle timer effect → fresh 15 min
  };

  const handleIdleSubmit = () => {
    setIdlePromptOpen(false);
    if (abandonTimerRef.current) {
      clearTimeout(abandonTimerRef.current);
      abandonTimerRef.current = null;
    }
    hasAutoSubmittedRef.current = true;
    setModalMode('idle');
  };

  const handleModalDismiss = () => {
    setModalMode('hidden');
  };

  return (
    <StyledBox key="Chat" coach={coach} data-testid="Chat">
      {isInitialLoading && <LoadingInterview />}
      <LeftSide>
        <Image
          alt="triangles"
          src={`${process.env.PUBLIC_URL}/media/images/4-white-dots.png`}
          width={15}
          style={{ position: 'absolute', bottom: '200px', left: '30px' }}
        />
        <Image
          alt="logo"
          src={`${process.env.PUBLIC_URL}/media/images/logo.png`}
          style={{
            position: 'absolute',
            top: '80px',
            left: '80px',
            width: '120%',
            maxWidth: '120%',
          }}
        />
      </LeftSide>
      <RightSide>
        <div style={{ flex: 1 }} />
        <Image
          alt="circles"
          src={`${process.env.PUBLIC_URL}/media/images/circles.png`}
          width={150}
          style={{ position: 'absolute', top: '-50px', right: '-50px' }}
        />
        <Image
          alt="purple_dots"
          src={`${process.env.PUBLIC_URL}/media/images/purple-triangles.png`}
          style={{ alignSelf: 'end', marginTop: '50px', marginRight: '70px' }}
          width={25}
        />
        <div style={{ flex: 1 }} />
        <Image
          alt="white_dots"
          src={`${process.env.PUBLIC_URL}/media/images/star.png`}
          style={{ alignSelf: 'end', marginBottom: '20px', marginRight: '40px' }}
          width={80}
        />
      </RightSide>
      <Image
        alt="venn"
        src={`${process.env.PUBLIC_URL}/media/images/venn-graph.png`}
        style={{
          position: 'absolute',
          left: '-5%',
          width: '120%',
          maxWidth: '120%',
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'row', height: '100%', zIndex: 1, justifyContent: 'center' }}>
        <ChatWrapper>
        <StyledContainer coach={coach}>
          <ul ref={chat}>
            {messageList.map((message, index) => (
              <li key={index}>
                <ReactMarkdown>{message}</ReactMarkdown>
              </li>
            ))}
            {user.status !== STATUS.RUNNING &&
              (!question ? null : (
                <li>
                  <ReactMarkdown>{question.replaceAll('\n', '\n\n')}</ReactMarkdown>
                </li>
              ))}
            {user.status === STATUS.RUNNING && (
              <li>
                <Typing />
              </li>
            )}
          </ul>
          <InputBox>
            <Input
              ref={text}
              disabled={user.status === STATUS.RUNNING}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setInput(event.target.value);
                event.target.style.height = 'auto';
                event.target.style.height = `${event.target.scrollHeight}px`;
              }}
              onKeyDown={handleEnter}
              placeholder={isTranscribing ? 'Transcribing…' : 'Type your message here!'}
              value={input}
            />
            <MicButton
              onClick={handleMicClick}
              recording={isRecording}
              disabled={user.status === STATUS.RUNNING || isTranscribing}
              title={isRecording ? 'Stop recording' : 'Speak your message'}
            >
              <Icon name={isRecording ? 'times' : 'start-recording'} width={18} />
            </MicButton>
            <Button onClick={handleSend} disabled={user.status === STATUS.RUNNING}>
              <Icon name="send" width={20} />
            </Button>
          </InputBox>
        </StyledContainer>
        <ToggleTab
          open={panelOpen}
          hint={showChatOrientation}
          onClick={() => { setPanelOpen(p => !p); }}
          title={panelOpen ? 'Close tools' : 'Open tools'}
        >
          {panelOpen ? '◀' : '▶'}
        </ToggleTab>
        <ToolsDrawer open={panelOpen}>
          <Header
            onSubmitClick={() => { handleSubmitClick(); setPanelOpen(false); }}
          />
          <DrawerDivider />
          <DrawerFooter>
            <DrawerLink
              as="button"
              onClick={() => { setShowAbout(true); setPanelOpen(false); }}
            >
              About Ikigai
            </DrawerLink>
            <DrawerLink
              as="button"
              onClick={() => { setShowAboutIntegralEd(true); setPanelOpen(false); }}
            >
              About Integral Ed
            </DrawerLink>
          </DrawerFooter>
        </ToolsDrawer>
        </ChatWrapper>
      </div>

      {showChatOrientation && (
        <ChatOrientationModal onDismiss={() => setShowChatOrientation(false)} />
      )}
      {modalMode !== 'hidden' && (
        <SubmitModal
          initialEmail={email}
          firstName={firstName}
          sessionId={sessionId}
          messages={messages[coach]}
          trigger={modalMode}
          onDismiss={handleModalDismiss}
        />
      )}
      {idlePromptOpen && (
        <IdleCheckModal
          onContinue={handleIdleContinue}
          onSubmit={handleIdleSubmit}
        />
      )}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
      {showAboutIntegralEd && <AboutIntegralEdModal onClose={() => setShowAboutIntegralEd(false)} />}
    </StyledBox>
  );
}

export default Chat;
